import React, { useId } from 'react';
import { User } from 'lucide-react';

interface NameInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  helperText?: string;
  type?: 'firstName' | 'lastName';
}

export function NameInput({
  value,
  onChange,
  onBlur,
  label,
  error,
  required = false,
  disabled = false,
  placeholder,
  className = '',
  helperText,
  type = 'firstName'
}: NameInputProps) {
  
  const id = useId();

  // Fonction pour formater le nom
  const formatName = (name: string): string => {
    if (!name) return '';
    
    // Supprimer les caractères non autorisés
    const cleaned = name.replace(/[^a-zA-ZÀ-ÿ\s'-]/g, '');
    
    if (type === 'firstName') {
      // Pour le prénom : capitaliser chaque mot
      return cleaned
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    } else {
      // Pour le nom : tout en majuscules (convention française)
      return cleaned.toUpperCase();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatName(inputValue);
    
    const event = {
      ...e,
      target: {
        ...e.target,
        value: formattedValue
      }
    };
    
    onChange(event as React.ChangeEvent<HTMLInputElement>);
  };

  const inputClasses = `input-field pl-10 ${error ? 'input-error' : ''} ${className}`;

  return (
    <div className="field-group">
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="form-label-required">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholder || (type === 'firstName' ? 'Jean' : 'DUPONT')}
          className={`${inputClasses} focus-visible`}
          disabled={disabled}
          autoComplete={type === 'firstName' ? 'given-name' : 'family-name'}
          maxLength={50}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200">
          <User className={`h-4 w-4 ${error ? 'text-error-500' : 'text-neutral-400'}`} />
        </div>
      </div>

      {helperText && !error && <p className="helper-text">{helperText}</p>}

      {error && <p id={`${id}-error`} className="error-message">{error}</p>}
    </div>
  );
}